import React, { useEffect } from "react";
import styled, { css, keyframes } from "styled-components";
import { Link } from "react-router-dom";
import GalleryItem from "./gallery_item";
import { useGetGalleryQuery } from "../../services/rtkApi/galleryApi";
import { useGetPendingArtworksQuery } from "../../services/rtkApi/artistsGalleryApi";
import { useAppSelector } from "../../store/hooks";
import { url } from "../../api";
import { ContributorsStrip } from "../../features/artistsGallery/ContributorsStrip";

import { logEvent, logPageView } from "../../analytics/GoogleAnalytics";

function checkRole(str, searchStr) {
  // convert the input string to an array by splitting at each comma
  try {
    var arr = (str || "").split(",");
  } catch {
    return false;
  }

  // loop through the array and check if any element matches the search string
  for (var i = 0; i < arr.length; i++) {
    if (arr[i].trim() === searchStr) {
      return true;
    }
  }
  return false;
}

const GalleryPage = (props) => {
  const { data: gallery = [], isLoading } = useGetGalleryQuery();
  const user = useAppSelector((state) => state.auth);

  useEffect(() => {
    document.title = 'NΛSOS Gallery'
    logPageView();
  }, []);

  const handleImageClick = (title) => {
    logEvent("click_image", {
      image_title: title,
      category: "Gallery",
    });
  };

  const isAdmin = checkRole(user.role, "admin");
  const isContributor = checkRole(user.role, "contributor");
  const canAddNassosArt = isAdmin || checkRole(user.role, "gallery_manager");
  const authReady = user.bootstrapStatus === "ready";
  const adminPendingEnabled = authReady && !!user.token && isAdmin;
  const { data: pendingArtworks = [] } = useGetPendingArtworksQuery(undefined, {
    skip: !adminPendingEnabled,
  });
  const pendingCount = pendingArtworks.length;

  return (
    <>
      <GalleryContainer>
        {/* Tillana was loaded from Google Fonts here but never applied; removed to keep visitor IPs off Google. */}
        <link
          rel="stylesheet"
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css"
        ></link>

        <HeaderRow>
          <TitleCluster>
            <TitleDiv>
              {user.token ? "gallery" : "gallery (login to see more)"}
            </TitleDiv>
            <ContributorsStrip />
          </TitleCluster>
          <HeaderActions>
            {isAdmin && (
              <ActionLink to="/admin/artworks/review" title="review pending artworks">
                <i className="fa fa-check-square-o" aria-hidden="true"></i> review queue
                {pendingCount > 0 ? ` (${pendingCount})` : ""}
              </ActionLink>
            )}
            {canAddNassosArt && (
              <AddArtButton to="/art/add" title="add a new piece to the main gallery">
                <button className="fa fa-plus" aria-hidden="true"></button>
              </AddArtButton>
            )}
          </HeaderActions>
        </HeaderRow>

        {isContributor && (
          <ActionRow>
            <ActionLink to="/art/contribute" title="contribute as a guest artist">
              <i className="fa fa-paint-brush" aria-hidden="true"></i> contribute
            </ActionLink>
          </ActionRow>
        )}
        {isLoading && (
          <WallNote $pulse aria-live="polite">
            loading the walls…
          </WallNote>
        )}

        {!isLoading && gallery.length === 0 && (
          <WallNote>nothing on the walls yet</WallNote>
        )}

        <Grid>
          {gallery.map((art, index) => {
            return (
              <GalleryItem
                key={art._id}
                picture={url + `/api/gallery/images/${art.imageFilename}`}
                title={art.title}
                description={art.description}
                dateMade={art.dateMade}
                creator={art.creator}
                onClick={() => handleImageClick(art.title)}
              />
            );
          })}
        </Grid>
      </GalleryContainer>
    </>
  );
};

const GalleryContainer = styled.div`
  background-color: #b286ffff;
  padding: 45px;
  position: relative;
`;

const wallNotePulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
`;

/* one-line note that fills the void under the hero while fetching / when the
   wall is empty. same hand-made box as the rest of the page. */
const WallNote = styled.div`
  display: inline-block;
  margin-top: 10px;
  padding: 8px 14px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 15px;
  font-weight: 700;
  color: #000000;
  background: #fff77a;
  border: var(--hm-border);
  border-radius: var(--hm-radius);
  box-shadow: var(--hm-shadow);
  ${(props) =>
    props.$pulse
      ? css`
          animation: ${wallNotePulse} 1.4s ease-in-out infinite;
        `
      : ""}
`;

const Grid = styled.div`
  display: grid;
  margin-top: 10px;
  grid-gap: 4rem;
  grid-template-columns: repeat(auto-fit, minmax(18rem, 0.5fr));

  @media screen and (max-width: 600px) {
    grid-template-columns: 1fr;
    justify-items: center;
  }
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  flex-wrap: wrap;
`;

const ActionLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  color: #000000;
  text-decoration: none;
  background: #fff77a;
  border: var(--hm-border);
  border-radius: var(--hm-radius);
  padding: 4px 10px;
  box-shadow: var(--hm-shadow-sm);
  transition: transform 0.1s ease, box-shadow 0.1s ease;

  i {
    font-size: 13px;
  }

  &:hover {
    transform: var(--hm-press-translate);
    box-shadow: var(--hm-shadow-sm-pressed);
    color: #000000;
  }
`;

const AddArtButton = styled(Link)`
  display: flex;
  font-size: 20px;
  color: #000000;
  text-decoration: none;
  font-weight: 600;
  justify-content: end;
  overflow: hidden;
  transition: font-size 0.1s;

  &:hover {
    color: #555;
    font-size: 22px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  padding-bottom: 20px;
`;

const TitleCluster = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 12px 18px;
  min-width: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  flex-wrap: wrap;
`;

const TitleDiv = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
`;

export default GalleryPage;
